import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { customerService, CustomerProfile } from '../services/customerService';
import { authService } from '../services';

interface CreditHoldContextValue {
  creditHold: boolean;
  customerProfile: CustomerProfile | null;
  isLoading: boolean;
  /** Mark the login-time dialog as seen for this session. */
  markLoginDialogShown: () => void;
  /** True if the once-per-session login dialog has not been shown yet. */
  loginDialogPending: boolean;
  /** Force re-fetch (e.g. after a sync). */
  refresh: () => Promise<void>;
}

const LOGIN_DIALOG_FLAG = 'creditHoldLoginDialogShown';

const CreditHoldContext = createContext<CreditHoldContextValue | undefined>(undefined);

export const useCustomerCreditHold = (): CreditHoldContextValue => {
  const ctx = useContext(CreditHoldContext);
  if (!ctx) {
    throw new Error('useCustomerCreditHold must be used within CreditHoldProvider');
  }
  return ctx;
};

export const CreditHoldProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginDialogPending, setLoginDialogPending] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setCustomerProfile(null);
      setIsLoading(false);
      return;
    }

    try {
      const profile = await customerService.getProfile();
      setCustomerProfile(profile);
      if (profile.credit_hold) {
        const alreadyShown = sessionStorage.getItem(LOGIN_DIALOG_FLAG) === 'true';
        if (!alreadyShown) setLoginDialogPending(true);
      } else {
        sessionStorage.removeItem(LOGIN_DIALOG_FLAG);
      }
    } catch (error) {
      // Backoffice/admin users may not have a customer profile — that's fine.
      setCustomerProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const markLoginDialogShown = useCallback(() => {
    sessionStorage.setItem(LOGIN_DIALOG_FLAG, 'true');
    setLoginDialogPending(false);
  }, []);

  return (
    <CreditHoldContext.Provider
      value={{
        creditHold: !!customerProfile?.credit_hold,
        customerProfile,
        isLoading,
        markLoginDialogShown,
        loginDialogPending,
        refresh: fetchProfile,
      }}
    >
      {children}
    </CreditHoldContext.Provider>
  );
};
